/**
 * 字体 Design Token 系统
 * 定义完整的字体系统规范
 */

import type { TypographyTokens } from './types';

// 字体 Token 定义
export const typographyTokens: TypographyTokens = {
  // 字体族
  fontFamily: {
    primary: '"Source Han Sans CN", "Noto Sans CJK SC", "PingFang SC", "Microsoft YaHei", sans-serif',
    secondary: '"Roboto", "Helvetica Neue", Arial, sans-serif',
    mono: '"Source Code Pro", "Consolas", "Monaco", monospace',
  },
  
  // 字体大小 (基于 16px 基准)
  fontSize: {
    xs: 12,      // 0.75rem - 说明文字
    sm: 14,      // 0.875rem - 辅助信息
    base: 16,    // 1rem - 标准正文 (基准)
    lg: 18,      // 1.125rem - 重要描述
    xl: 20,      // 1.25rem - 小标题
    '2xl': 24,   // 1.5rem - H4 卡片标题
    '3xl': 30,   // 1.875rem - H3 子区块标题
    '4xl': 36,   // 2.25rem - H2 区块标题
    '5xl': 48,   // 3rem - H1 页面主标题
    '6xl': 60,   // 3.75rem - 超大标题
  },
  
  // 字体粗细
  fontWeight: {
    light: 300,     // 装饰性文字、引言
    normal: 400,    // 正文内容、默认文字
    medium: 500,    // 次要强调、按钮文字
    semibold: 600,  // 重要强调、小标题
    bold: 700,      // 标题文字、重要信息
    extrabold: 800, // 特殊强调、品牌标题
  },
  
  // 行高
  lineHeight: {
    tight: 1.25,    // 紧密行高 - 标题文字
    normal: 1.5,     // 标准行高 - 正文内容
    relaxed: 1.75,   // 宽松行高 - 长文本阅读
  },
};

// 字体使用规范
export const typographyUsage = {
  // 字体大小应用场景
  fontSizeUsage: {
    xs: '提示文字、版权信息、次要标签',
    sm: '辅助信息、表单标签、按钮文字',
    base: '正文内容、列表项、表格内容',
    lg: '重要描述、引言、摘要',
    xl: '小标题、卡片副标题',
    '2xl': '卡片标题、对话框标题',
    '3xl': '子区块标题、页面子标题',
    '4xl': '区块标题、页面次要标题',
    '5xl': '页面主标题、重要标题',
  },
  
  // 字体粗细应用场景
  fontWeightUsage: {
    300: '引言、装饰性文字、次要信息',
    400: '正文内容、描述文字、常规信息',
    500: '按钮文字、导航项、次要强调',
    600: '小标题、重要信息、强调文字',
    700: '标题文字、重要标题、关键信息',
    800: '品牌标题、特殊强调、超级标题',
  },
  
  // 行高应用场景
  lineHeightUsage: {
    1.25: '标题文字、按钮文字、导航项',
    1.5: '正文内容、列表项、表格内容',
    1.75: '长文本阅读、文章内容、说明文档',
  },
};

// 响应式字体系统
export const responsiveTypography = {
  // 移动端字体调整
  mobile: {
    fontSize: {
      xs: 12,
      sm: 14,
      base: 16,
      lg: 18,
      xl: 20,
      '2xl': 22,
      '3xl': 24,
      '4xl': 28,
      '5xl': 36,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.6,
    },
  },
  
  // 平板端字体调整
  tablet: {
    fontSize: {
      xs: 12,
      sm: 14,
      base: 16,
      lg: 18,
      xl: 20,
      '2xl': 24,
      '3xl': 27,
      '4xl': 32,
      '5xl': 42,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.45,
      relaxed: 1.65,
    },
  },
  
  // 桌面端字体 (默认)
  desktop: typographyTokens,
};

// 导出字体常量，用于 SCSS 变量兼容
export const typographyConstants = {
  // 字体族
  FONT_FAMILY_PRIMARY: typographyTokens.fontFamily.primary,
  FONT_FAMILY_SECONDARY: typographyTokens.fontFamily.secondary,
  FONT_FAMILY_MONO: typographyTokens.fontFamily.mono,
  
  // 字体大小
  FONT_SIZE_XS: typographyTokens.fontSize.xs,
  FONT_SIZE_SM: typographyTokens.fontSize.sm,
  FONT_SIZE_BASE: typographyTokens.fontSize.base,
  FONT_SIZE_LG: typographyTokens.fontSize.lg,
  FONT_SIZE_XL: typographyTokens.fontSize.xl,
  FONT_SIZE_2XL: typographyTokens.fontSize['2xl'],
  FONT_SIZE_3XL: typographyTokens.fontSize['3xl'],
  FONT_SIZE_4XL: typographyTokens.fontSize['4xl'],
  FONT_SIZE_5XL: typographyTokens.fontSize['5xl'],
  FONT_SIZE_6XL: typographyTokens.fontSize['6xl'],
  
  // 字体粗细
  FONT_WEIGHT_LIGHT: typographyTokens.fontWeight.light,
  FONT_WEIGHT_NORMAL: typographyTokens.fontWeight.normal,
  FONT_WEIGHT_MEDIUM: typographyTokens.fontWeight.medium,
  FONT_WEIGHT_SEMIBOLD: typographyTokens.fontWeight.semibold,
  FONT_WEIGHT_BOLD: typographyTokens.fontWeight.bold,
  FONT_WEIGHT_EXTRABOLD: typographyTokens.fontWeight.extrabold,
  
  // 行高
  LINE_HEIGHT_TIGHT: typographyTokens.lineHeight.tight,
  LINE_HEIGHT_NORMAL: typographyTokens.lineHeight.normal,
  LINE_HEIGHT_RELAXED: typographyTokens.lineHeight.relaxed,
} as const;